import React, { } from 'react';
import Accordion from '../../shared/Accordion';
import { ResourceStrings } from '../../../ResourceStrings';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { Dialog, OperationState } from '../../../store/appState';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import * as Actions from '../../../store/actions';
import PostDetail from '../PostDetail';
import { App } from '../../../app';
import { useHistory, useLocation } from 'react-router-dom';
import { forMinWidth } from '../../../theme/breakpoints';
import { RootPost } from '../../../RootPost';
import { DeflectInfo } from './SubmitPostView';

export interface SearchResultsProps {
    deflectInfo: DeflectInfo,
    onSearch: (searchFor: string, updateCountsByType: boolean, resultsToSkip: number) => void;
}

export default function SearchResults(props: SearchResultsProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const searchText = useSelector((state: RootState) => state.appState.problems.searchtext);
    const history = useHistory();
    const location = useLocation();
    const userId = useSelector((state: RootState) => state.appState.userVsid);

    const list = state.problems.results.results;
    const totalMatches = state.problems.results.countsByType ? state.problems.results.countsByType.problem + state.problems.results.countsByType.idea : 0;

    const onJoinDiscussion = (post: RootPost): void => {
        // @TODO: Do we need this call to prepare anything for submission?
        // It is clearing up the selected values like syslogs, screenshot
        // @TODO: Change the legacy setDialog call with something which gel better with browser's back navigation
        // For now push a new url with unused param to have 'back' action bring it back to this page
        Actions.setDialog(Dialog.comment);

        const params = new URLSearchParams(location.search);
        params.set("dialog", "comment");
        history.push(`${history.location.pathname}?${params}`);
    };

    const onUpvote = (post: RootPost): void => {
        Actions.voteFor(post);
    };

    const updateCountsByType: boolean = searchText !== App.GetActiveSearchInfo(state).searchtext;

    return state.problems.searchtext && totalMatches !== 0 && <Wrapper
        data-cy="rap-search-results-wrapper"
        isLoading={state.problems.state === OperationState.Executing}
        isExpandedByDefault={true}
        highlightWhenCollapsed
        animationDelay="none"
        titleIcon="CRMCustomerInsightsApp"
        title={ResourceStrings.searchResultsTitle(totalMatches)}>
        {list.map((item, i) => (
            <Accordion
                data-cy={`rap-search-results-list-${i}`}
                key={i}
                onExpandedToggle={(isExpanded) => {
                    if (isExpanded) {
                        Actions.initDetailsView(item);
                        Actions.loadTicket(item.answerHubId);
                        App.telemetry.deflect(props.deflectInfo, "expand_suggestion", {
                            "position": (i + 1).toString(),
                            "state": item.state,
                            "trackingId": item.trackingId ?? "",
                            "title": item.title ?? "",
                            "url": item.communityUrl ?? "",
                            "solution_count": item.solutionCount?.toString() ?? "",
                            "comment_count": item.commentCount?.toString() ?? "",
                        });
                    }
                }}
                expanderLocation="left"
                className="post-detail-accordion"
                title={<Title>
                    <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: App.EscapeHtmlRawDisplay(item.title) }}
                    />
                </Title>}
            >
                <PostDetail 
                    deflectInfo={props.deflectInfo}
                    post={item}
                    postV3={state.ticketsById[item.answerHubId]}
                    signInState={state.signInState}
                    userId={userId}
                    onJoinDiscussion={onJoinDiscussion}
                    onUpvote={onUpvote} />
            </Accordion>
        ))}
        {list.length < totalMatches && <LoadMore>
            <Button
                data-cy="rap-load-more-btn"
                buttonType="link"
                onClick={() => props.onSearch(searchText, updateCountsByType, list.length)}>
                {ResourceStrings.loadMore()}
            </Button>
        </LoadMore>}
    </Wrapper>
}

const Wrapper = styled(Accordion) <{ isLoading: boolean }>`
    &>.accordion-content {
        max-height: 75rem;
        overflow: auto;
    }

    opacity: ${({ isLoading }) => isLoading ? "0.5" : "1"};

    .post-detail-accordion {
        margin-left: 1rem;
        margin-right: 1rem;

        ${forMinWidth.md`
            margin-left: 3rem;
            margin-right: 3.5rem;
        `}

        ${forMinWidth.lg`
            margin-left: 5.7rem;
            margin-right: 6.8rem;
        `}

        .accordion-header.expanded {
            font-weight: 600;
        }
    }

    .post-detail-accordion + .post-detail-accordion {
        border-top: solid 1px  var(--color-on-surface-lighter);
    }
`;

const Title = styled.div`
    display: flex;
    justify-content: space-between;

    .right-icon {
        margin-left: 2rem;
    }
`;

const LoadMore = styled.div`
    text-align: center;
    margin: 0.8rem 0;

    button {
        font-size: 1.4rem;
    }
`;